// ---------------------------------------------------------------------------
// Route-aware breadcrumb generation
// Derives breadcrumb trail from current pathname + navigation store context.
// No per-page wiring needed — layout renders this automatically.
// ---------------------------------------------------------------------------

import { IN_APP_TOOLS } from '../../config/inAppTools';

export interface BreadcrumbItem {
  label: string;
  path?: string;  // clickable if present
}

interface NavContext {
  projectId: number | null;
  projectName: string | null;
  fileId: number | null;
  fileName: string | null;
  moleculeId: number | null;
  moleculeName: string | null;
}

/** Strip internal hash prefix from file names (e.g. "d7ee8d87_test.sdf" → "test.sdf") */
function stripFileHash(name: string): string {
  return name.replace(/^[a-f0-9]{8,}_/i, '');
}

const ADMIN_LABELS: Record<string, string> = {
  users: 'Users',
  tenants: 'Tenants',
  analytics: 'Analytics',
  audit: 'Audit Log',
  sharing: 'Sharing Activity',
  'reference-library': 'Reference Library',
  'field-defaults': 'Field Defaults',
  security: 'Security Alerts',
  'org-settings': 'Organisation Settings',
  'registration-rules': 'Registration Rules',
};

export function getBreadcrumbs(
  pathname: string,
  ctx: NavContext,
  searchParams: URLSearchParams,
): BreadcrumbItem[] {
  const crumbs: BreadcrumbItem[] = [];

  // --- Chemistry ---
  if (pathname === '/chemistry') {
    crumbs.push({ label: 'Chemistry' });
    return crumbs;
  }

  if (pathname === '/chemistry/projects') {
    crumbs.push({ label: 'Chemistry', path: '/chemistry' });
    crumbs.push({ label: 'Projects' });
    return crumbs;
  }

  if (pathname === '/chemistry/files') {
    crumbs.push({ label: 'Chemistry', path: '/chemistry' });
    const pid = searchParams.get('project') || ctx.projectId;
    if (pid && ctx.projectName) {
      crumbs.push({ label: ctx.projectName });
    } else if (pid) {
      crumbs.push({ label: `Project ${pid}` });
    } else {
      crumbs.push({ label: 'Files' });
    }
    return crumbs;
  }

  // Editor: /chemistry/file/:id/editor
  const editorMatch = pathname.match(/^\/chemistry\/file\/([^/]+)\/editor$/);
  if (editorMatch) {
    crumbs.push({ label: 'Chemistry', path: '/chemistry' });
    if (ctx.projectId && ctx.projectName) {
      crumbs.push({ label: ctx.projectName, path: `/chemistry/files?project=${ctx.projectId}&view=files` });
    } else if (ctx.projectId) {
      crumbs.push({ label: `Project ${ctx.projectId}`, path: `/chemistry/files?project=${ctx.projectId}&view=files` });
    }
    const displayFileName = ctx.fileName ? stripFileHash(ctx.fileName) : `File ${editorMatch[1]}`;
    crumbs.push({ label: displayFileName });
    return crumbs;
  }

  if (pathname === '/chemistry/quick-search') {
    crumbs.push({ label: 'Chemistry', path: '/chemistry' });
    crumbs.push({ label: 'Quick Search' });
    return crumbs;
  }

  if (pathname === '/chemistry/registry') {
    crumbs.push({ label: 'Chemistry', path: '/chemistry' });
    crumbs.push({ label: 'Registry' });
    return crumbs;
  }

  if (pathname === '/chemistry/reactions') {
    crumbs.push({ label: 'Chemistry', path: '/chemistry' });
    crumbs.push({ label: 'Reactions' });
    return crumbs;
  }

  if (pathname.startsWith('/chemistry/reactions/')) {
    crumbs.push({ label: 'Chemistry', path: '/chemistry' });
    crumbs.push({ label: 'Reactions', path: '/chemistry/reactions' });
    crumbs.push({ label: 'Reaction Detail' });
    return crumbs;
  }

  // --- Workbench ---
  if (pathname === '/workbench' || pathname === '/hubs') {
    // If we have project context, show chemistry ancestry
    if (ctx.projectId) {
      crumbs.push({ label: 'Chemistry', path: '/chemistry' });
      if (ctx.projectName) {
        crumbs.push({ label: ctx.projectName, path: `/chemistry/files?project=${ctx.projectId}&view=files` });
      }
    }
    crumbs.push({ label: 'Toolbox' });
    return crumbs;
  }

  if (pathname.startsWith('/workbench/')) {
    const toolSlug = pathname.replace('/workbench/', '');
    const tool = IN_APP_TOOLS.find(t => t.path === pathname);
    const toolLabel = tool?.title || toolSlug.charAt(0).toUpperCase() + toolSlug.slice(1);

    // If arriving with project context, show full ancestry
    if (ctx.projectId) {
      crumbs.push({ label: 'Chemistry', path: '/chemistry' });
      if (ctx.projectName) {
        crumbs.push({ label: ctx.projectName, path: `/chemistry/files?project=${ctx.projectId}&view=files` });
      }
      if (ctx.fileId && ctx.fileName) {
        crumbs.push({ label: stripFileHash(ctx.fileName), path: `/chemistry/file/${ctx.fileId}/editor` });
      }
    }
    crumbs.push({ label: 'Toolbox', path: '/hubs' });
    crumbs.push({ label: toolLabel });
    return crumbs;
  }

  // --- Biology / Assays ---
  if (pathname === '/biology/assays') {
    crumbs.push({ label: 'Assays' });
    return crumbs;
  }

  if (pathname.startsWith('/biology/assays/')) {
    crumbs.push({ label: 'Assays', path: '/biology/assays' });
    crumbs.push({ label: 'Assay Detail' });
    return crumbs;
  }

  // --- Genomics ---
  if (pathname === '/genomics') {
    crumbs.push({ label: 'Genomics' });
    return crumbs;
  }

  // --- Omics (labelled "Biologics" to match nav bar) ---
  if (pathname === '/omics') {
    crumbs.push({ label: 'Biologics' });
    return crumbs;
  }

  // --- Pipeline ---
  if (pathname === '/pipeline') {
    crumbs.push({ label: 'Pipeline' });
    return crumbs;
  }

  // --- Inventory ---
  if (pathname === '/inventory') {
    crumbs.push({ label: 'Inventory' });
    return crumbs;
  }

  // --- Batches ---
  if (pathname === '/batches') {
    crumbs.push({ label: 'Batches' });
    return crumbs;
  }

  // --- Spectra ---
  if (pathname === '/spectra') {
    crumbs.push({ label: 'Spectra' });
    return crumbs;
  }

  // --- Dashboard ---
  if (pathname === '/dashboard') {
    crumbs.push({ label: 'Dashboard' });
    return crumbs;
  }

  // --- Files ---
  if (pathname === '/files') {
    crumbs.push({ label: 'All Files' });
    return crumbs;
  }

  // --- Admin ---
  if (pathname.startsWith('/admin')) {
    crumbs.push({ label: 'Admin', path: '/admin/users' });
    const sub = pathname.replace('/admin/', '').replace('/admin', '');
    if (sub && sub !== 'users') {
      // Check for tenant detail: /admin/tenants/:id
      const tenantMatch = sub.match(/^tenants\/(\d+)$/);
      if (tenantMatch) {
        crumbs.push({ label: 'Tenants', path: '/admin/tenants' });
        crumbs.push({ label: `Tenant ${tenantMatch[1]}` });
        return crumbs;
      }
      const label = ADMIN_LABELS[sub] || sub.charAt(0).toUpperCase() + sub.slice(1);
      crumbs.push({ label });
    } else {
      crumbs.push({ label: 'Users' });
    }
    return crumbs;
  }

  // --- Molecule / Protein detail ---
  if (pathname.startsWith('/molecule/')) {
    crumbs.push({ label: 'Chemistry', path: '/chemistry' });
    crumbs.push({ label: ctx.moleculeName || 'Molecule Detail' });
    return crumbs;
  }

  if (pathname.startsWith('/protein/')) {
    crumbs.push({ label: 'Biologics', path: '/omics' });
    crumbs.push({ label: 'Protein Detail' });
    return crumbs;
  }

  return crumbs;
}
